import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { CityFilter, type CityOption } from "../city-filter";
import {
  MAX_ROUTE_LEGS,
  fitRoute,
  nextRouteCity,
  routeDayCount,
  type RouteLeg,
} from "../../lib/trip-route";
import { MAX_TRIP_DAYS } from "./trip-when-fields";

/**
 * "Where": the trip's route, a city and its days per leg (owner, 2026-11-01:
 * the route is asked at the start).
 *
 * One city looks exactly as it always did, the city menu on its own, with the
 * day count still asked below by "How many days?". "Add another city" turns
 * it into a list: each city with its own day stepper, removable, in order.
 * When the guest picked dates the total is fixed by them, so the last city
 * shows what is left rather than a stepper (`fitRoute`).
 */
export function TripRouteFields({
  cities,
  legs,
  fixedTotal,
  onChange,
}: {
  cities: readonly CityOption[];
  legs: readonly RouteLeg[];
  /** Days the dates fix, or null when there are no dates. */
  fixedTotal: number | null;
  onChange: (next: RouteLeg[]) => void;
}) {
  const { t } = useTranslation();
  const multi = legs.length > 1;
  const total = fixedTotal ?? routeDayCount(legs);
  const set = (next: RouteLeg[]) => onChange(fitRoute(next, fixedTotal));
  const canAdd =
    legs.length < MAX_ROUTE_LEGS &&
    (fixedTotal === null ? total < MAX_TRIP_DAYS : fixedTotal > legs.length);
  const nameOf = (slug: string) => cities.find((c) => c.slug === slug)?.name ?? slug;

  const addLeg = () => {
    const city = nextRouteCity(legs, cities.map((c) => c.slug));
    if (!city) return;
    // With dates the new city takes a day from the last one; without, it is
    // one more day on the trip.
    set([...legs, { city, days: 1 }]);
  };

  return (
    <div className="mj-trip-dialog__field mj-trip-route">
      <span id="mj-trip-route-label">{t(multi ? "trip.route.label" : "trip.trip_city")}</span>

      {multi ? (
        <ol className="mj-trip-route__legs" aria-labelledby="mj-trip-route-label">
          {legs.map((leg, i) => {
            const last = i === legs.length - 1;
            const lockDays = fixedTotal !== null && last;
            const city = nameOf(leg.city);
            return (
              <li key={i} className="mj-trip-route__leg">
                <div className="mj-trip-route__city">
                  <CityFilter
                    cities={cities}
                    selectedSlug={leg.city}
                    onSelect={(slug) =>
                      slug && set(legs.map((l, j) => (j === i ? { ...l, city: slug } : l)))
                    }
                    includeAll={false}
                    compact
                    triggerLabel={t("trip.route.city_n", { n: i + 1 }) + ": " + city}
                  />
                </div>
                {lockDays ? (
                  <strong className="mj-trip-route__rest">
                    {t("trip.route.days", { count: leg.days })}
                  </strong>
                ) : (
                  <div
                    className="mj-trip-dialog__days mj-trip-route__days"
                    role="group"
                    aria-label={t("trip.route.days_in", { city })}
                  >
                    <button
                      type="button"
                      onClick={() => set(legs.map((l, j) => (j === i ? { ...l, days: l.days - 1 } : l)))}
                      disabled={leg.days <= 1}
                      aria-label={t("trip.route.fewer_days", { city })}
                    >
                      <Icon name="ri-subtract-line" aria-hidden="true" />
                    </button>
                    <strong aria-live="polite">{leg.days}</strong>
                    <button
                      type="button"
                      onClick={() => set(legs.map((l, j) => (j === i ? { ...l, days: l.days + 1 } : l)))}
                      disabled={
                        fixedTotal === null
                          ? total >= MAX_TRIP_DAYS
                          : (legs[legs.length - 1]?.days ?? 0) <= 1
                      }
                      aria-label={t("trip.route.more_days", { city })}
                    >
                      <Icon name="ri-add-line" aria-hidden="true" />
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="mj-trip-route__remove"
                  onClick={() => set(legs.filter((_, j) => j !== i))}
                  aria-label={t("trip.route.remove", { city })}
                >
                  <Icon name="ri-close-line" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ol>
      ) : (
        <CityFilter
          cities={cities}
          selectedSlug={legs[0]?.city || null}
          onSelect={(slug) => slug && set([{ city: slug, days: legs[0]?.days ?? 1 }])}
          includeAll={false}
        />
      )}

      {canAdd ? (
        <button type="button" className="mj-trip-route__add" onClick={addLeg}>
          <Icon name="ri-add-line" aria-hidden="true" />
          {t("trip.route.add_city")}
        </button>
      ) : null}
      {multi ? (
        <p className="mj-trip-dialog__hint">{t("trip.route.total", { count: total })}</p>
      ) : null}
    </div>
  );
}
